import { coerceValue, VALUE_TYPES } from '@/utils/valueTypeCoercion.js';

export const OPERATOR_LABELS = {
  eq: 'equals',
  ne: 'not equal to',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  between: 'between',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  before: 'before',
  after: 'after',
  on_or_before: 'on or before',
  on_or_after: 'on or after',
  is_set: 'is set',
  is_not_set: 'is not set',
};

export const NO_VALUE_OPERATORS = ['is_set', 'is_not_set'];

export function emptyFilters() {
  return {
    projects: [],
    locations: [],
    instruments: [],
    materials: [],
    relationTypes: [],
    startDate: '',
    endDate: '',
    metadataLists: [],
    metadata: [],
    materialMetadataLists: [],
    materialMetadata: [],
  };
}

export function cloneFilters(filters) {
  return JSON.parse(JSON.stringify(filters));
}

let rowCounter = 0;
export function newMetadataRow() {
  rowCounter += 1;
  return { uid: `md-${rowCounter}`, key: null, operator: '', value: '', value2: '' };
}

function convertSingle(raw, valueType) {
  if (raw === '' || raw === null || raw === undefined) {
    return { status: 'empty' };
  }
  let input = raw;
  // datetime-local inputs have no seconds or zone
  if (valueType === VALUE_TYPES.DATETIME && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(input)) {
    input = `${input}:00Z`;
  }
  const result = coerceValue(input, valueType);
  if (!result.valid) return { status: 'invalid', error: result.error };

  switch (valueType) {
    case VALUE_TYPES.INTEGER:
    case VALUE_TYPES.FLOAT:
      return { status: 'ok', value: Number(String(result.coercedValue).replace(',', '.')) };
    case VALUE_TYPES.BOOLEAN:
      return { status: 'ok', value: result.coercedValue === 'true' };
    default:
      return { status: 'ok', value: result.coercedValue };
  }
}

/** Returns { status: 'empty' | 'invalid' | 'ok', filter?, error? } for one metadata row. */
export function validateMetadataRow(row) {
  if (!row.key || !row.operator) return { status: 'empty' };

  if (NO_VALUE_OPERATORS.includes(row.operator)) {
    return { status: 'ok', filter: { metadata_key_id: row.key.id, operator: row.operator } };
  }

  const valueType = row.key.value_type;
  const first = convertSingle(row.value, valueType);

  if (row.operator === 'between') {
    const second = convertSingle(row.value2, valueType);
    if (first.status === 'empty' && second.status === 'empty') return { status: 'empty' };
    if (first.status === 'invalid') return first;
    if (second.status === 'invalid') return second;
    if (first.status === 'empty' || second.status === 'empty') {
      return { status: 'invalid', error: 'Both values are required' };
    }
    return {
      status: 'ok',
      filter: {
        metadata_key_id: row.key.id,
        operator: row.operator,
        value: first.value,
        value_to: second.value,
      },
    };
  }

  if (first.status !== 'ok') return first;
  return {
    status: 'ok',
    filter: { metadata_key_id: row.key.id, operator: row.operator, value: first.value },
  };
}

export function buildFilterPayload(filters) {
  const ids = (list) => list.map((item) => item.id);
  const payload = {};

  if (filters.projects.length) payload.project_ids = ids(filters.projects);
  if (filters.locations.length) payload.location_ids = ids(filters.locations);
  if (filters.instruments.length) payload.instrument_ids = ids(filters.instruments);
  if (filters.materials.length) {
    payload.material_ids = ids(filters.materials);
    if (filters.relationTypes.length) {
      payload.material_relation_type_ids = ids(filters.relationTypes);
    }
  }

  if (filters.startDate || filters.endDate) {
    payload.date_range = {};
    if (filters.startDate) payload.date_range.start = `${filters.startDate}T00:00:00Z`;
    if (filters.endDate) payload.date_range.end = `${filters.endDate}T23:59:59Z`;
  }

  // Keep both namespaces separate; material IDs are optional for material predicates.
  for (const [field, apiField] of [['metadata', 'metadata'], ['materialMetadata', 'material_metadata']]) {
    const predicates = (filters[field] || [])
      .map(validateMetadataRow)
      .filter((result) => result.status === 'ok')
      .map((result) => result.filter);
    if (predicates.length) payload[apiField] = predicates;
  }

  return payload;
}
